(ns leg.sidebar
  "The feeds, and how much of each one is left.

  One list, by name, and no folders. A category was a second place to put a
  feed and a second thing to keep tidy; what somebody actually does here is
  look for a name, and forty names in alphabetical order are found faster
  than forty names in nine groups.

  Groups do not change that. A group is not a drawer this list opens, it is
  another way *in* - a row beside `All feeds` that leads to a place of its
  own with its own address and its own list of articles. So the names stay
  flat and alphabetical under a block of ways in, and a feed in a group is
  still in the list where its name is.

  A feed that could not be fetched says so here rather than in a dialog
  somewhere: it is a property of the feed, it is where the feed is, and it
  is what somebody wants to know before wondering why nothing new arrived."
  (:require-macros [kc.core :refer [def-ui $]])
  (:require [clojure.string :as str]
            [leg.mui :as mui :refer [sx]]
            [leg.nav :as nav]
            [leg.state :as state]
            [leg.theme :as theme]
            [leg.util :as u]))

(def-ui Count
  "The unread number, and nothing when there is none. A zero is a thing to
  read and then ignore."
  [n]
  (when (pos? (or n 0))
    ($ :span {:style {:flex "none" :fontSize ".8rem" :opacity ".75"
                      :fontVariantNumeric "tabular-nums"}}
       (str n))))

(def-ui Trouble
  "A feed that could not be fetched, said where the feed is.

  Drawn rather than typed, and labelled with what actually went wrong: an
  exclamation mark is a character, a tooltip is for a mouse, and somebody
  reading this window with a screen reader needs the sentence."
  [error]
  ($ mui/Tooltip {:title error}
     ($ mui/Box {:component "span"
                 :role "img"
                 :aria-label (str "Could not be fetched: " error)
                 :sx (sx {:flex "none" :display "flex" :color "error.main"})}
        ($ :svg {:width ".9rem" :height ".9rem" :viewBox "0 0 24 24"
                 :aria-hidden true :focusable "false"}
           ($ :path {:d "M12 3.5 L22.5 20.5 L1.5 20.5 Z"
                     :fill "none" :stroke "currentColor" :strokeWidth 2
                     :strokeLinejoin "round"})
           ($ :path {:d "M12 9.5 L12 14.5" :stroke "currentColor"
                     :strokeWidth 2 :strokeLinecap "round"})
           ($ :circle {:cx 12 :cy 17.7 :r 1.1 :fill "currentColor"})))))

(def-ui Row [id title unread error? selected? on-select]
  ($ mui/ListItemButton
     {:selected (boolean selected?)
      ;; What the keyboard walks along, as in the article list (leg.keys).
      :className "leser-row"
      :onClick (fn [_] (on-select id))
      :sx (sx {:alignItems "center" :gap ".5rem"
               :paddingTop ".35rem" :paddingBottom ".35rem"
               "@media (pointer: coarse)" {:minHeight "2.75rem"}})}
     ;; A paper's name is set in the paper's face, like the headlines in
     ;; it - the same serif the article list uses one column to the right,
     ;; and the same one a reader chose under Settings. The names of the
     ;; papers are the one part of this column that is the newspaper and
     ;; not the furniture around it; the count and the warning beside them
     ;; still are.
     ($ :span {:style {:flex "1" :minWidth 0 :overflow "hidden"
                       :textOverflow "ellipsis" :whiteSpace "nowrap"
                       :fontFamily theme/font-head
                       :fontWeight (if (pos? (or unread 0)) 700 600)
                       :fontSize ".95rem" :letterSpacing "-.01em"}}
        title)
     (when error? ($ Trouble {:error error?}))
     ($ Count {:n unread})))

(defn- shown
  "Which names are listed under this filter.

  Under `unread`, the ones with something left in them - and three that are
  there whatever their count says. The feed that is open, because a feed
  must not vanish from under somebody who is reading it the moment they
  mark it read. The one that could not be fetched, because its count is
  zero for the one reason worth showing: nothing arrived. And `All feeds`,
  which is not a feed and is drawn on its own below."
  [feeds mode selected]
  (if (= :all mode)
    feeds
    (filter (fn [f] (or (pos? (or (:feed/unread f) 0))
                        (:feed/error f)
                        (= selected (:feed/id f))))
            feeds)))

(defn- groups
  "The ways in that are groups: a name, what is left to read across the
  feeds carrying it, and whether one of them is in trouble.

  Summed here and not asked for, because the feeds are already here with
  their counts - a second number from the server would be a second chance
  for the two to disagree. Sorted by name, which is the only order a group
  has: nothing records when one was made, because nothing records a group
  at all.

  The same rule as a feed decides whether a group is drawn under the unread
  filter, and for the same reason: a group somebody is standing in must not
  vanish when they clear it."
  [feeds mode selected]
  (->> feeds
       (keep :feed/group)
       distinct
       (map (fn [g]
              (let [in (filter #(= g (:feed/group %)) feeds)]
                {:group  g
                 :unread (reduce + 0 (map #(or (:feed/unread %) 0) in))
                 :error  (some :feed/error in)})))
       (filter (fn [{:keys [group unread error]}]
                 (or (= :all mode) (pos? unread) error (= selected group))))
       (sort-by #(str/lower-case (:group %)))))

(def-ui Sidebar
  "The whole column: the ways in, then the papers.

  The ways in are `All feeds` and the groups, in one block over a rule.
  They are the same kind of thing - a list made of several feeds, with an
  address and a count - so they are drawn with the same row and sit
  together, and the rule says where they stop and the papers begin."
  [feeds filter selected-feed selected-group on-select on-select-group]
  (let [total  (reduce + 0 (map #(or (:feed/unread %) 0) feeds))
        listed (shown feeds filter selected-feed)
        gs     (groups feeds filter selected-group)]
    ($ mui/List {:dense true :disablePadding true :data-keys "list"
                 :sx (sx {:paddingBottom "1rem"})}
       ;; Everything, always first and always there. It is where a reader
       ;; starts, and it is the only row that is still useful when there is
       ;; not a single feed yet.
       ($ Row {:id nil :title "All feeds" :unread total
               :selected? (and (nil? selected-feed) (nil? selected-group))
               :on-select on-select})
       (for [{:keys [group unread error]} gs]
         ($ Row {:key (str "group:" group)
                 :id group
                 :title group
                 :unread unread
                 :error? error
                 :selected? (= selected-group group)
                 :on-select on-select-group}))
       ;; A hairline and not a subheading: the block above is ways in and
       ;; the block below is papers, which is a thing to see rather than a
       ;; thing to read twice in every window.
       (when (seq gs)
         ($ mui/Divider {:sx (sx {:margin ".35rem 0"})}))
       (for [f (sort-by #(str/lower-case (u/feed-label %)) listed)]
         ($ Row {:key (:feed/id f)
                 :id (:feed/id f)
                 :title (u/feed-label f)
                 :unread (:feed/unread f)
                 :error? (:feed/error f)
                 :selected? (= selected-feed (:feed/id f))
                 :on-select on-select}))
       ;; Said, rather than left as a list with one row in it: a column that
       ;; is suddenly empty reads as something gone wrong, and this is the
       ;; opposite - there is nothing left to read.
       (when (and (seq feeds) (empty? listed))
         ($ mui/Box {:sx (sx {:padding "1rem .75rem" :color "text.secondary"
                              :fontSize ".85rem"})}
            "Nothing unread.")))))

(def-ui Masthead
  "The paper's name, where a paper has it: at the top of the page, in the
  display serif, over a double rule. It is the one place in this window
  that is allowed to be ornament - everything under it is a list."
  []
  ($ mui/Box {:sx (sx {:padding ".55rem .75rem .45rem"
                       :borderBottom "3px double" :borderColor "divider"})}
     ($ :span {:style {:fontFamily theme/font-head :fontWeight 900
                       :fontSize "1.45rem" :lineHeight 1 :letterSpacing "-.015em"}}
        "Leser")))

(def-ui SidebarPane
  "The feeds, as the window's navigation. A landmark and not a column of
  divs: it is the one list in here that is a way to somewhere else, and a
  screen reader should be able to jump to it and past it.

  Under it the same bar every screen has (`leg.nav`), and everything that is
  not reading is behind the one button on its right: adding a feed, the
  collection, the keys, the readers. A row of four buttons under a list of
  forty feeds is a second navigation nobody asked for - this costs a click and
  buys back the corner of the window."
  []
  (let [{:keys [feeds feed group feed-filter version settings]} (state/use-state)]
    ($ mui/Box {:className "leser-vt-nav"
                :sx (sx {:height "100%" :display "flex" :flexDirection "column"
                         :overflow "hidden"})}
       ($ Masthead)
       ($ mui/Box {:component "nav"
                   :aria-label "Feeds"
                   :sx (sx {:flex "1" :minHeight 0 :overflowY "auto"})}
          ($ Sidebar {:feeds feeds :filter feed-filter
                      :selected-feed feed :selected-group group
                      :on-select state/select-feed!
                      :on-select-group state/select-group!}))
       ($ nav/Bar
          {:label "Feeds"
           :left ($ nav/Home)
           ;; The same two words in the same place as over the articles, and
           ;; two and not three: a feed is not starred.
           :middle ($ nav/Filters {:current feed-filter
                                   :options [[:unread "Unread"] [:all "All"]]
                                   :on-change state/set-feed-filter!})
           :right ($ mui/Tooltip
                     {:title (str "Leser " (or version "") " - "
                                  (or (:reader/email settings) ""))}
                     ($ mui/Button {:onClick (fn [_] (state/open-settings!))}
                        "Settings"))}))))
